import { useSyncExternalStore } from 'react';
import {
  COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY,
  resolveAutoCommunicationsView,
  resolveCommunicationsView,
  type AutoCommunicationsView,
} from '../utils/view-preference.util';
import type { CommunicationsView } from 'src/modules/shared/types/communications-view.type';

/**
 * Result returned by the Communications view resolution hook.
 */
export interface UseResolvedCommunicationsViewResult {
  /** Auto-selected view for the current viewport. */
  autoView: AutoCommunicationsView;
  /** Effective view used by the page, respecting explicit selection when present. */
  resolvedView: CommunicationsView;
  /** Whether the current view still comes from auto-selection. */
  isAutoView: boolean;
}

interface LegacyMediaQueryList extends MediaQueryList {
  addListener: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener: (listener: (event: MediaQueryListEvent) => void) => void;
}

const subscribeToCommunicationsViewport = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const mediaQueryList = window.matchMedia(
    COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY
  );
  const handleChange = () => onStoreChange();

  if ('addEventListener' in mediaQueryList) {
    mediaQueryList.addEventListener('change', handleChange);

    return () => mediaQueryList.removeEventListener('change', handleChange);
  }

  const legacyMediaQueryList = mediaQueryList as LegacyMediaQueryList;

  legacyMediaQueryList.addListener(handleChange);

  return () => legacyMediaQueryList.removeListener(handleChange);
};

/**
 * Resolves the Communications view, using a small-screen auto-default when the
 * route/search state has no explicit view.
 *
 * @param view - Explicit route/search-selected view, if any.
 * @returns Auto and resolved view state for the current viewport.
 */
export const useResolvedCommunicationsView = (
  view?: CommunicationsView
): UseResolvedCommunicationsViewResult => {
  const autoView = useSyncExternalStore<AutoCommunicationsView>(
    subscribeToCommunicationsViewport,
    resolveAutoCommunicationsView,
    resolveAutoCommunicationsView
  );

  return {
    autoView,
    resolvedView: resolveCommunicationsView(view),
    isAutoView: view === undefined,
  };
};
